import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';
import './Management.css';

const ServiceManagement = () => {
  const { token } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [debugStatus, setDebugStatus] = useState('');
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // File Input Keys to force DOM unmount/reset on clear
  const [imageInputKey, setImageInputKey] = useState(Date.now());
  const [videoInputKey, setVideoInputKey] = useState(Date.now());

  // Image states
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImageIds, setExistingImageIds] = useState([]);

  // Video states
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [existingVideoIds, setExistingVideoIds] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
  });

  useEffect(() => {
    fetchServices();
  }, [token]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (submitting) {
        e.preventDefault();
        e.returnValue = 'Upload in progress...';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [submitting]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      console.log('🔄 [DEBUG] Fetching services from:', `${API_URL}/api/services?populate=*`);
      const response = await fetch(`${API_URL}/api/services?populate=*`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      console.log('📋 [DEBUG] Services Fetched:', data);
      setServices(data.data || []);
    } catch (err) {
      console.error('❌ [DEBUG] Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const uploadMedia = async (file, isImage = true) => {
    const fileTypeLabel = isImage ? 'IMAGE' : 'VIDEO';
    console.log(`🚀 [DEBUG] Starting ${fileTypeLabel} upload...`, {
      fileName: file.name,
      fileSize: `${(file.size / (1024 * 1024)).toFixed(2)} MB`,
      fileType: file.type,
    });
    setDebugStatus(`Uploading ${fileTypeLabel} (${(file.size / (1024 * 1024)).toFixed(1)} MB)...`);

    let uploadFile = file;
    if (isImage) {
      try {
        console.log('🎨 [DEBUG] Normalizing image canvas...');
        const bitmap = await createImageBitmap(file);
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        canvas.getContext('2d').drawImage(bitmap, 0, 0);
        bitmap.close();
        uploadFile = await new Promise((resolve) =>
          canvas.toBlob(
            (blob) => resolve(new File([blob], file.name, { type: blob.type })),
            file.type || 'image/jpeg',
            0.92
          )
        );
      } catch (e) {
        console.warn('⚠️ [DEBUG] Canvas normalization skipped:', e);
      }
    }

    const data = new FormData();
    data.append('files', uploadFile);

    const response = await fetch(`${API_URL}/api/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: data,
    });

    console.log(`📡 [DEBUG] Upload response status: ${response.status}`);
    if (!response.ok) {
      const errText = await response.text();
      console.error(`❌ [DEBUG] Upload failed (${response.status}):`, errText);
      throw new Error(`Media upload failed (${response.status}): ${errText}`);
    }

    const result = await response.json();
    console.log('✅ [DEBUG] Upload success, received Media ID:', result[0]?.id);
    return result[0].id;
  };

  const formatMediaUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
    const path = url.startsWith('/') ? url : `/${url}`;
    return `${baseUrl}${path}`;
  };

  const resetForm = () => {
    setFormData({ title: '', description: '' });

    setImageFile(null);
    setImagePreview(null);
    setExistingImageIds([]);
    setImageInputKey(Date.now());

    setVideoFile(null);
    setVideoPreview(null);
    setExistingVideoIds([]);
    setVideoInputKey(Date.now());

    setEditingId(null);
    setShowForm(false);
    setSubmitting(false);
    setDebugStatus('');
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (submitting) return;

    setError('');
    setSubmitting(true);
    setDebugStatus('Preparing data...');

    try {
      const method = editingId ? 'PUT' : 'POST';
      const endpoint = editingId
        ? `${API_URL}/api/services/${editingId}`
        : `${API_URL}/api/services`;

      const descriptionBlocks = formData.description
        ? [
            {
              type: 'paragraph',
              children: [{ type: 'text', text: formData.description }],
            },
          ]
        : [];

      let imageIds = [...existingImageIds];
      if (imageFile) {
        setDebugStatus('Uploading image...');
        const newImageId = await uploadMedia(imageFile, true);
        imageIds = [newImageId];
      }

      let videoIds = [...existingVideoIds];
      if (videoFile) {
        setDebugStatus('Uploading video...');
        const newVideoId = await uploadMedia(videoFile, false);
        videoIds = [newVideoId];
      }

      const imagePayload = imageIds.length > 0 ? imageIds[0] : null;
      const videoPayload = videoIds.length > 0 ? videoIds[0] : null;

      const payload = {
        data: {
          title: formData.title,
          description: descriptionBlocks,
          image: imagePayload,
          video: videoPayload,
        },
      };

      setDebugStatus('Saving service...');

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || errorData.message || `Save failed (${response.status})`);
      }

      setDebugStatus('Updating list...');

      await fetchServices();
      resetForm();

    } catch (err) {
      console.error('💥 Error in handleSubmit:', err);
      setError(`Submit Error: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Weet je zeker dat je deze dienst wilt verwijderen?')) return;

    try {
      const response = await fetch(`${API_URL}/api/services/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Kan dienst niet verwijderen');
      await fetchServices();
    } catch (err) {
      setError(err.message);
    }
  };

  const getDescriptionText = (desc) => {
    if (!desc) return '';
    if (typeof desc === 'string') return desc;
    if (Array.isArray(desc)) {
      return desc
        .map(block => {
          if (block.children && Array.isArray(block.children)) {
            return block.children.map(child => child.text || '').join(' ');
          }
          return '';
        })
        .join(' ');
    }
    return '';
  };

  const extractMediaData = (mediaProp) => {
    if (!mediaProp) return { ids: [], url: null };

    const rawData = mediaProp.data !== undefined ? mediaProp.data : mediaProp;
    if (!rawData) return { ids: [], url: null };

    if (Array.isArray(rawData)) {
      const ids = rawData.map(item => item.id);
      const firstUrl = rawData[0]?.attributes?.url || rawData[0]?.url || null;
      return { ids, url: firstUrl };
    }

    const id = rawData.id;
    const url = rawData.attributes?.url || rawData.url || null;
    return { ids: id ? [id] : [], url };
  };

  const handleEdit = (service) => {
    const attrs = service.attributes || service;

    const imageData = extractMediaData(attrs.image);
    const videoData = extractMediaData(attrs.video);

    setFormData({
      title: attrs.title || '',
      description: getDescriptionText(attrs.description),
    });

    setExistingImageIds(imageData.ids);
    setImagePreview(formatMediaUrl(imageData.url));
    setImageFile(null);

    setExistingVideoIds(videoData.ids);
    setVideoPreview(formatMediaUrl(videoData.url));
    setVideoFile(null);

    setEditingId(service.id);
    setShowForm(true);
  };

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Diensten</h1>
        <button 
          type="button" 
          className="btn-primary" 
          onClick={() => showForm ? resetForm() : setShowForm(true)}
        >
          {showForm ? 'Annuleren' : '+ Dienst Toevoegen'}
        </button>
      </div>

      {error && (
        <div className="error-message" style={{ background: '#fed7d7', color: '#9b2c2c', padding: '12px', borderRadius: '6px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {showForm && (
        <div className="admin-form">
          <div className="form-group">
            <label>Dienstnaam</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label>Beschrijving</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="4"
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label>Afbeelding</label>
            {imagePreview && (
              <div style={{ marginBottom: '8px' }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{ maxHeight: '120px', maxWidth: '200px', borderRadius: '4px', objectFit: 'cover' }}
                />
                <button
                  type="button"
                  style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#e53e3e', background: 'none', border: 'none', cursor: 'pointer' }}
                  onClick={() => { setImageFile(null); setImagePreview(null); setExistingImageIds([]); setImageInputKey(Date.now()); }}
                  disabled={submitting}
                >
                  Afbeelding verwijderen
                </button>
              </div>
            )}
            <input
              key={imageInputKey}
              type="file"
              accept="image/*"
              disabled={submitting}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setImageFile(file);
                  setImagePreview(URL.createObjectURL(file));
                }
              }}
            />
          </div>

          <div className="form-group">
            <label>Video</label>
            {videoFile ? (
              <div style={{ marginBottom: '8px', padding: '8px', background: '#edf2f7', borderRadius: '4px' }}>
                <div style={{ fontSize: '13px', fontWeight: 'bold' }}>📹 {videoFile.name}</div>
                <div style={{ fontSize: '12px', color: '#4a5568' }}>
                  {(videoFile.size / (1024 * 1024)).toFixed(2)} MB
                </div>
                <button
                  type="button"
                  style={{ marginTop: '4px', fontSize: '12px', color: '#e53e3e', background: 'none', border: 'none', cursor: 'pointer' }}
                  onClick={() => { setVideoFile(null); setVideoPreview(null); setExistingVideoIds([]); setVideoInputKey(Date.now()); }}
                  disabled={submitting}
                >
                  Video verwijderen
                </button>
              </div>
            ) : videoPreview ? (
              <div style={{ marginBottom: '8px', padding: '8px', background: '#edf2f7', borderRadius: '4px' }}>
                <div style={{ fontSize: '12px', color: '#4a5568' }}>Bestaande video gekoppeld</div>
                <button
                  type="button"
                  style={{ marginTop: '4px', fontSize: '12px', color: '#e53e3e', background: 'none', border: 'none', cursor: 'pointer' }}
                  onClick={() => { setVideoFile(null); setVideoPreview(null); setExistingVideoIds([]); setVideoInputKey(Date.now()); }}
                  disabled={submitting}
                >
                  Video verwijderen
                </button>
              </div>
            ) : null}

            <input
              key={videoInputKey}
              type="file"
              accept="video/*"
              disabled={submitting}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  console.log('🎥 [DEBUG] Video selected:', file.name, `${(file.size / (1024 * 1024)).toFixed(2)} MB`);
                  setVideoFile(file);
                }
              }}
            />
          </div>

          {debugStatus && (
            <div style={{ padding: '8px 12px', background: '#e2e8f0', color: '#2d3748', borderRadius: '4px', fontSize: '14px', marginBottom: '12px', fontWeight: 'bold' }}>
              ⏳ {debugStatus}
            </div>
          )}

          <button 
            type="button" 
            className="btn-submit" 
            disabled={submitting}
            onClick={handleSubmit}
          >
            {submitting ? 'Aan het verwerken...' : editingId ? 'Dienst Bijwerken' : 'Dienst Aanmaken'}
          </button>
        </div>
      )}

      {loading ? (
        <p className="loading">Diensten worden geladen...</p>
      ) : services.length === 0 ? (
        <p className="empty">Geen diensten gevonden</p>
      ) : (
        <div className="items-table">
          <table>
            <thead>
              <tr>
                <th>Media</th>
                <th>Naam</th>
                <th>Beschrijving</th>
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {services.map((service) => {
                const attrs = service.attributes || service;
                const title = attrs.title || '';
                const descText = getDescriptionText(attrs.description).substring(0, 50) || '-';

                const imageData = extractMediaData(attrs.image);
                const thumbUrl = formatMediaUrl(imageData.url);

                const videoData = extractMediaData(attrs.video);
                const videoUrl = formatMediaUrl(videoData.url);

                return (
                  <tr key={service.id}>
                    <td>
                      {videoUrl ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#2b6cb0' }}>🎥 Video</span>
                          <a href={videoUrl} target="_blank" rel="noreferrer" style={{ fontSize: '11px', color: '#3182ce', textDecoration: 'underline' }}>
                            Bekijk
                          </a>
                        </div>
                      ) : thumbUrl ? (
                        <img src={thumbUrl} alt={title} style={{ width: '60px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} />
                      ) : (
                        <span style={{ color: '#aaa', fontSize: '12px' }}>Geen media</span>
                      )}
                    </td>
                    <td>{title}</td>
                    <td>{descText}{descText.length > 50 ? '...' : ''}</td>
                    <td className="actions">
                      <button className="btn-edit" onClick={() => handleEdit(service)}>
                        Bewerken
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(service.id)}>
                        Verwijderen
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ServiceManagement;